import React, { useEffect, useState } from "react";
import { DEFAULT_ATTRIBUTE_CONSTRAINT_VALUE } from "Components/GMS/Attribute/constants";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import TextField from "Components/DesignSystem/TextField/TextField";
import Toggle from "Components/DesignSystem/Toggle/Toggle";

/**
 * The constraint edit form — on the design system since the category module's
 * Phase 5. Both handlers are the legacy logic verbatim: `handleChange` is
 * keyed on `e.target.id`, and `handleCheckBoxChange` carries the
 * validation ↔ allowed_user_defined_values mutual exclusion. DS Dropdown and
 * Toggle don't emit change events, so their callbacks synthesize
 * `{ target: { id, value } }` (the § 72 reshaping, keyed on `id` because that
 * is what these handlers read). Which controls are live vs disabled is
 * unchanged: only Datatype, Validation, Multiselect and Allowed User Defined
 * Values are editable; everything else ships disabled, exactly as before.
 */
const EditCategoryAttributes = ({
  categoryAttributes,
  setCategoryAttributes,
}) => {
  const [category, setCategory] = useState(categoryAttributes);

  useEffect(() => {
    let newValues = {...categoryAttributes};
    Object.keys(categoryAttributes).forEach((key) => {
      if (categoryAttributes[key] === null) {
        newValues[key] = DEFAULT_ATTRIBUTE_CONSTRAINT_VALUE[key];
      }
    });
    setCategory(newValues);
  }, []);

  useEffect(() => {
    setCategoryAttributes(category);
  }, [category]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setCategory((currentCategory) => ({
      ...currentCategory,
      [id]: value,
    }));
  };

  const handleCheckBoxChange = (e, checked) => {
    const id = e.target.id;
    setCategory((currentCategory) => {
      if (id === "validation") {
        return {
          ...currentCategory,
          validation: checked,
          allowed_user_defined_values: !checked,
        };
      }
      if (id === "allowed_user_defined_values") {
        return {
          ...currentCategory,
          allowed_user_defined_values: checked,
          validation: !checked,
        };
      }
      return {
        ...currentCategory,
        [id]: checked,
      };
    });
  };

  return (
    <form className="tw-grid tw-grid-cols-12 tw-gap-4">
      <div className="tw-col-span-12">
        <Dropdown
          label="Datatype"
          size="sm"
          data-testid="category-edit-datatype-select"
          options={[
            { value: "INTEGER", label: "INTEGER" },
            { value: "TEXT", label: "TEXT" },
            { value: "DECIMAL", label: "DECIMAL" },
            { value: "BOOLEAN", label: "BOOLEAN" },
          ]}
          value={category.datatype}
          onChange={(value) =>
            handleChange({ target: { id: "datatype", value } })
          }
        />
      </div>

      <div className="tw-col-span-12">
        <Toggle
          label="Validation"
          id="validation"
          data-testid="category-attribute-validation-toggle"
          checked={category?.validation ?? false}
          onChange={() =>
            handleCheckBoxChange(
              { target: { id: "validation" } },
              !category.validation
            )
          }
        />
      </div>

      <div className="tw-col-span-3">
        <Dropdown
          label="Validation Type"
          size="sm"
          data-testid="components-editcategoryattributes-select-1"
          disabled={!category.validation}
          options={[{ value: "LOV", label: "LOV" }]}
          value={category?.validation_type}
          onChange={(value) =>
            handleChange({ target: { id: "validation_type", value } })
          }
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Validation Range"
          size="sm"
          type="number"
          disabled={true}
          value={category?.validation_range ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "validation_range", value } })
          }
          placeholder="e.g 1-5"
          id="validation_range"
          data-testid="category-constraint-validation_range-input"
        />
      </div>
      <div className="tw-col-span-5">
        <TextField
          label="Validation Type Extra Criteria"
          size="sm"
          type="text"
          disabled={true}
          value={category?.validation_type_extra_criteria ?? ""}
          onChange={(value) =>
            handleChange({
              target: { id: "validation_type_extra_criteria", value },
            })
          }
          placeholder="Enter validation type extra criteria"
          id="validation_type_extra_criteria"
          data-testid="category-constraint-validation_type_extra_criteria-input"
        />
      </div>

      <div className="tw-col-span-12">
        <Toggle
          label="Validation Input Message"
          id="validation_input_message"
          data-testid="components-editcategoryattributes-input-2"
          disabled={true}
          checked={category?.validation_input_message ?? false}
          onChange={() =>
            handleCheckBoxChange(
              { target: { id: "validation_input_message" } },
              !category.validation_input_message
            )
          }
        />
      </div>
      <div className="tw-col-span-6">
        <TextField
          label="Title"
          size="sm"
          type="text"
          disabled={true}
          value={category?.validation_input_message_title ?? ""}
          onChange={(value) =>
            handleChange({
              target: { id: "validation_input_message_title", value },
            })
          }
          placeholder="e.g Pro tip"
          id="validation_input_message_title"
          data-testid="category-constraint-validation_input_message_title-input"
        />
      </div>
      <div className="tw-col-span-6">
        <TextField
          label="Message"
          size="sm"
          type="text"
          disabled={true}
          value={category?.validation_input_message_body ?? ""}
          onChange={(value) =>
            handleChange({
              target: { id: "validation_input_message_body", value },
            })
          }
          placeholder="e.g Validate before you save"
          id="validation_input_message_body"
          data-testid="category-constraint-validation_input_message_body-input"
        />
      </div>

      <div className="tw-col-span-12">
        <Toggle
          label="Validation Error Alert"
          id="validation_error_alert"
          data-testid="components-editcategoryattributes-input-3"
          disabled={true}
          checked={category?.validation_error_alert ?? false}
          onChange={() =>
            handleCheckBoxChange(
              { target: { id: "validation_error_alert" } },
              !category.validation_error_alert
            )
          }
        />
      </div>
      <div className="tw-col-span-3">
        <Dropdown
          label="Style"
          size="sm"
          data-testid="components-editcategoryattributes-select-4"
          disabled={true}
          options={[
            { value: "STOP", label: "STOP" },
            { value: "WARNING", label: "WARNING" },
            { value: "INFO", label: "INFO" },
          ]}
          value={category.validation_error_alert_style}
          onChange={(value) =>
            handleChange({
              target: { id: "validation_error_alert_style", value },
            })
          }
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Title"
          size="sm"
          type="text"
          disabled={true}
          value={category.validation_error_alert_title ?? ""}
          onChange={(value) =>
            handleChange({
              target: { id: "validation_error_alert_title", value },
            })
          }
          placeholder="e.g Danger. Will Robinson!"
          id="validation_error_alert_title"
          data-testid="category-constraint-validation_error_alert_title-input"
        />
      </div>
      <div className="tw-col-span-5">
        <TextField
          label="Message"
          size="sm"
          type="text"
          disabled={true}
          value={category.validation_error_alert_body ?? ""}
          onChange={(value) =>
            handleChange({
              target: { id: "validation_error_alert_body", value },
            })
          }
          placeholder="e.g This doesn't seem like the right kind of information"
          id="validation_error_alert_body"
          data-testid="category-constraint-validation_error_alert_body-input"
        />
      </div>

      <div className="tw-col-span-12">
        <Toggle
          label="Multiselect"
          id="multiselect"
          data-testid="components-editcategoryattributes-input-5"
          checked={category?.multiselect ?? false}
          onChange={() =>
            handleCheckBoxChange(
              { target: { id: "multiselect" } },
              !category.multiselect
            )
          }
        />
      </div>
      <div className="tw-col-span-6">
        <TextField
          label="Number"
          size="sm"
          type="number"
          disabled={true}
          value={`${category.multiselect_number ?? ""}`}
          onChange={(value) =>
            handleChange({ target: { id: "multiselect_number", value } })
          }
          placeholder="e.g 4"
          id="multiselect_number"
          data-testid="category-constraint-multiselect_number-input"
        />
      </div>
      <div className="tw-col-span-6">
        <TextField
          label="Output Range"
          size="sm"
          type="text"
          disabled={true}
          value={category.multiselect_output_range ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "multiselect_output_range", value } })
          }
          placeholder="e.g 1-4"
          id="multiselect_output_range"
          data-testid="category-constraint-multiselect_output_range-input"
        />
      </div>

      <div className="tw-col-span-12">
        <Toggle
          label="Attribute Inheritance"
          id="attr_inheritance"
          data-testid="components-editcategoryattributes-input-6"
          disabled={true}
          checked={category?.attr_inheritance ?? false}
          onChange={() =>
            handleCheckBoxChange(
              { target: { id: "attr_inheritance" } },
              !category.attr_inheritance
            )
          }
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Child Attribute"
          size="sm"
          type="text"
          disabled={true}
          value={category.child_attr ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "child_attr", value } })
          }
          placeholder="None Entered"
          id="child_attr"
          data-testid="category-constraint-child_attr-input"
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Allowed Characters"
          size="sm"
          type="text"
          disabled={true}
          value={category.allowed_chars ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "allowed_chars", value } })
          }
          placeholder="None Entered"
          id="allowed_chars"
          data-testid="category-constraint-allowed_chars-input"
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Prohibited Characters"
          size="sm"
          type="text"
          disabled={true}
          value={category.prohibited_chars ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "prohibited_chars", value } })
          }
          placeholder="None Entered"
          id="prohibited_chars"
          data-testid="category-constraint-prohibited_chars-input"
        />
      </div>

      <div className="tw-col-span-12">
        <Toggle
          label="Allowed User Defined Values"
          id="allowed_user_defined_values"
          data-testid="components-editcategoryattributes-input-7"
          checked={category?.allowed_user_defined_values ?? false}
          onChange={() =>
            handleCheckBoxChange(
              { target: { id: "allowed_user_defined_values" } },
              !category.allowed_user_defined_values
            )
          }
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Extra Criteria Value 1"
          size="sm"
          type="text"
          disabled={true}
          value={category.extra_criteria_value1 ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "extra_criteria_value1", value } })
          }
          placeholder="None Entered"
          id="extra_criteria_value1"
          data-testid="category-constraint-extra_criteria_value1-input"
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Extra Criteria Value 2"
          size="sm"
          type="text"
          disabled={true}
          value={category.extra_criteria_value2 ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "extra_criteria_value2", value } })
          }
          placeholder="None Entered"
          id="extra_criteria_value2"
          data-testid="category-constraint-extra_criteria_value2-input"
        />
      </div>
      <div className="tw-col-span-4">
        <TextField
          label="Special Formatting"
          size="sm"
          type="text"
          disabled={true}
          value={category.special_formatting ?? ""}
          onChange={(value) =>
            handleChange({ target: { id: "special_formatting", value } })
          }
          placeholder="None Entered"
          id="special_formatting"
          data-testid="category-constraint-special_formatting-input"
        />
      </div>
    </form>
  );
};

export default EditCategoryAttributes;
